import { api } from '@/lib/api/client';

/**
 * PRD §16 — Atledi (Anthropic) wrapper.
 *
 * Two endpoints:
 *   • GET  /atledi/welcome  — Entity Home greeting (4h cache).
 *   • POST /atledi/recap    — Insights Recap card payload (4h cache per
 *                              KPI hash, so the same numbers don't spend
 *                              tokens twice).
 *
 * Both fall back deterministically when the backend has no Anthropic
 * key, so the FE never has to special-case "no AI". Treat `cached` as
 * a hint for debug UIs; consumer copy should stay identical regardless.
 */

export interface AtlediWelcomeResponse {
  greeting: string;
  cached?: boolean;
}

export interface AtlediRecapKpis {
  moments?: number | null;
  throughput?: number | null;
  inventory?: number | null;
}

export interface AtlediRecapPayload {
  narrative: string;
  kpiSummary: { label: string; value: string }[];
  recommendation: string;
  confidence: 'low' | 'medium' | 'high';
  cached?: boolean;
}

export interface AtlediCaptureRateResponse {
  rate: number | null;
  source: 'industry' | 'learned' | 'none';
}

export const atlediApi = {
  welcome: (params: {
    brandId?: number;
    brandName?: string;
    locale?: 'en' | 'es';
  }) =>
    api.get<AtlediWelcomeResponse>('/atledi/welcome', {
      params: {
        brandId: params.brandId,
        brandName: params.brandName,
        locale: params.locale,
      },
    }),

  recap: (input: {
    brandId?: number;
    brandName?: string;
    locale?: 'en' | 'es';
    eventId?: number;
    kpis: AtlediRecapKpis;
  }) => api.post<AtlediRecapPayload>('/atledi/recap', input),

  captureRate: (params: { brandId?: number; industry?: string }) =>
    api.get<AtlediCaptureRateResponse>('/atledi/capture-rate', {
      params: { brandId: params.brandId, industry: params.industry },
    }),
};
